import './styles/graphic.scss';
import mapboxgl from 'mapbox-gl';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';

let currentYear;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  monthsShort: [
    'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
    'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
  ],
  meridiem: (hour, minute, isLowercase) => (hour >= 12 ? 'p.m.' : 'a.m.'),
});


const fetchTornadoData = async () => {
  try {
    currentYear = dayjs().year();
    const url = `https://ix.cnn.io/data/tornado-locations/storm_reports_${currentYear}_tornadoes_slim.json`;
    const response = await fetch(url);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching tornado data:', error);
  }
};

const drawMap = async (container, isMobile) => {
  const tornadoData = await fetchTornadoData();
  const { lastUpdatedStr, msg } = tornadoData;

  document.getElementById('timestamp').textContent = `Last updated: ${lastUpdatedStr}`;
  const subhedDekElement = document.querySelector('.subhed.dek');
  if (subhedDekElement) {
    subhedDekElement.innerHTML = msg;
  }

  mapboxgl.accessToken = 'pk.eyJ1IjoiY25uZGlnaXRhbCIsImEiOiJiYjIwMTgxNDZmNjgyODllMjAxYTYwOTAxYmY2MzI2MyJ9.8F6TQITXymFzCXy8tBqGgg';

  const optionsSm = {
    initZoom: 2,
    minZoom: 2,
    maxZoom: 7,
    center: [-95.5, 38.5],
    maxBounds: [[-180, 0], [-20, 52]],
  };

  const optionsLg = {
    initZoom: 3.2,
    minZoom: 2,
    maxZoom: 7,
    center: [-97, 38.5],
    maxBounds: [[-180, 0], [-20, 52]],
  };

  const options = isMobile ? optionsSm : optionsLg;

  const map = new mapboxgl.Map({
    container: container.node(),
    style: 'mapbox://styles/cnndigital/clx22otit01hh01ppa0862d7z',
    center: options.center,
    zoom: options.initZoom,
    maxZoom: options.maxZoom,
    minZoom: options.minZoom,
    maxBounds: options.maxBounds,
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  map.scrollZoom.disable();

  if (!isMobile) {
    const nav = new mapboxgl.NavigationControl({
      showCompass: false,
    });
    map.addControl(nav, 'top-left');
  }

  const addHexGridLayer = () => {
    map.addSource('hex-grid', {
      type: 'vector',
      url: 'mapbox://cnndigital.tornadoes-hexgrid',
    });

    map.addLayer({
      id: 'hex-grid-layer',
      type: 'fill',
      source: 'hex-grid',
      'source-layer': 'Tornadoes - hexagon grid',
      paint: {
        'fill-color': [
          'step',
          ['get', 'tornado_count'],
          '#ff33c3', // tornado_count = 0
          1, '#ffd3c3', // tornado_count >= 1
          3, '#f89a8b', // tornado_count >= 3
          5, '#e6655a', // tornado_count >= 5
          9, '#c52622', // tornado_count >= 9
          15, '#670000', // tornado_count >= 15
        ],
        'fill-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0, [
            'step',
            ['get', 'tornado_count'],
            0, // tornado_count = 0
            1, 0.8, // tornado_count = 1-2
            3, 0.8, // tornado_count = 3-4
            5, 0.8, // tornado_count = 5-7
            9, 0.8, // tornado_count = 9-14
            15, 0.8, // tornado_count = 15+
          ],
          6, [
            'step',
            ['get', 'tornado_count'],
            0, // tornado_count = 0
            1, 0.8, // tornado_count = 1-2
            3, 0.8, // tornado_count = 3-4
            5, 0.8, // tornado_count = 5-7
            9, 0.8, // tornado_count = 9-14
            15, 0.8, // tornado_count = 15+
          ],
          7, [
            'step',
            ['get', 'tornado_count'],
            0, // tornado_count = 0
            1, 0.8, // tornado_count = 1-2
            3, 0.8, // tornado_count = 3-4
            5, 0.8, // tornado_count = 5-7
            9, 0.8, // tornado_count = 9-14
            15, 0.8, // tornado_count = 15+
          ],
        ],
        'fill-outline-color': '#ffffff',
      },
    });

    map.moveLayer('hex-grid-layer', 'jk-us-state-labels-v2');

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });

    map.on('mouseenter', 'hex-grid-layer', (e) => {
      const tornadoCount = e.features[0].properties.tornado_count;
      if (tornadoCount > 0) {
        map.getCanvas().style.cursor = 'pointer';
        const coordinates = e.lngLat;

        popup
          .setLngLat(coordinates)
          .setHTML(`<p>Tornado count in ${currentYear}: <strong>${tornadoCount}</strong></p>`)
          .addTo(map);
      }
    });

    map.on('mouseleave', 'hex-grid-layer', () => {
      map.getCanvas().style.cursor = '';
      popup.remove();
    });

    map.on('mousemove', 'hex-grid-layer', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['hex-grid-layer'],
      });

      if (features.length) {
        const feature = features[0];
        const tornadoCount = feature.properties.tornado_count;

        if (tornadoCount > 0) {
          popup
            .setLngLat(e.lngLat)
            .setHTML(`<p>Tornado count: <strong>${tornadoCount}</strong></p>`)
            .addTo(map);
        } else {
          popup.remove();
        }
      } else {
        popup.remove();
      }
    });
  };

  window.pymChild && window.pymChild.sendHeight();

  map.on('load', () => {
    addHexGridLayer();
  });
};

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
  } = props;

  container.html(''); // Clear previous content
  container.append('div')
    .attr('id', 'map')
    .style('width', `${initialWidth}px`)
    .style('height', `${initialHeight}px`);

  drawMap(container.select('#map'), isMobile); // Pass the D3 selection of the map div
}
